<template>
  <div class="row gx-0 mb-30">
    <div class="col-md-6">
      <div class="filter-left d-flex align-items-center flex-wrap">
        <button
          type="button"
          class="btn style-two"
          data-bs-toggle="modal"
          data-bs-target="#expenseFilterModal"
        >
          {{ $t("expense_page.filter") }} <img src="../../../assets/img/icons/filter.svg" :alt="$t('expense_page.filter')" />
        </button>
        <form @submit.prevent="handleSearch" class="search-area position-relative w-sm-100">
          <input
            v-model="searchTerm"
            type="text"
            :placeholder="$t('expense_page.search_on_this_table')"
            class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white"
            @input="handleSearch"
          />
          <button
            type="submit"
            class="bg-transparent border-0 position-absolute top-0 end-0 h-100 pt-0 py-0 px-2"
          >
            <img src="../../../assets/img/icons/search.svg" alt="Image" />
          </button>
        </form>
      </div>
    </div>
    <div class="col-md-6">
      <div class="filter-right d-flex align-items-center justify-content-md-end flex-wrap">
        <a href="javascript:void(0)" class="btn style-three" @click="$emit('export-excel')">{{ $t("expense_page.excel") }}</a>
        <router-link :to="btnLink" class="btn style-one">
          {{ $t("expense_page.create") }} {{ $t(btnText) }}
          <img src="../../../assets/img/icons/add-circle.svg" :alt="$t('expense_page.create')" />
        </router-link>
      </div>
    </div>
  </div>

  <div
    class="modal fade"
    id="expenseFilterModal"
    tabindex="-1"
    aria-labelledby="expenseFilterModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title text-title" id="expenseFilterModalLabel">{{ $t("expense_page.filter_expenses") }}</h5>
          <button
            type="button"
            class="btn-close p-0"
            data-bs-dismiss="modal"
            :aria-label="$t('common.close')"
          >
            <img src="../../../assets/img/icons/close-circle-2.svg" :alt="$t('common.close')" />
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="applyFilter">
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group mb-15">
                  <label class="d-block fs-14 text-black mb-10">{{ $t("expense_page.warehouse") }}</label>
                  <select
                    v-model="filters.warehouse_id"
                    class="bg_ash border-0 rounded-1 fs-14 text-optional w-100 p-2"
                  >
                    <option value="">{{ $t("expense_page.all_warehouses") }}</option>
                    <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
                      {{ warehouse.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group mb-15">
                  <label class="d-block fs-14 text-black mb-10">{{ $t("expense_page.category") }}</label>
                  <select
                    v-model="filters.category_id"
                    class="bg_ash border-0 rounded-1 fs-14 text-optional w-100 p-2"
                  >
                    <option value="">{{ $t("expense_page.all_categories") }}</option>
                    <option v-for="category in categories" :key="category.id" :value="category.id">
                      {{ category.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group mb-15">
                  <label class="d-block fs-14 text-black mb-10">{{ $t("expense_page.from_date") }}</label>
                  <input
                    v-model="filters.from_date"
                    type="date"
                    class="w-100 d-block shadow-none fs-14 bg_ash rounded-1 text-black border-0 placeholder-1 p-2"
                  />
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group mb-15">
                  <label class="d-block fs-14 text-black mb-10">{{ $t("expense_page.to_date") }}</label>
                  <input
                    v-model="filters.to_date"
                    type="date"
                    class="w-100 d-block shadow-none fs-14 bg_ash rounded-1 text-black border-0 placeholder-1 p-2"
                  />
                </div>
              </div>

              <div class="col-lg-6 mb-md-15">
                <button
                  type="button"
                  class="btn style-four w-100 d-block"
                  data-bs-dismiss="modal"
                  @click="resetFilter"
                >
                  {{ $t("expense_page.reset") }}
                </button>
              </div>
              <div class="col-lg-6">
                <button
                  type="submit"
                  class="btn style-five w-100 d-block"
                  data-bs-dismiss="modal"
                >
                  {{ $t("expense_page.apply_filter") }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import expenseService, { type ExpenseCategory, type ExpenseFilters } from "../../../services/expense";
import warehouseService, { type Warehouse } from "../../../services/warehouse";

const createEmptyFilters = (): ExpenseFilters => ({
  warehouse_id: "",
  category_id: "",
  from_date: "",
  to_date: "",
});

export default defineComponent({
  name: "FilterContent",
  props: ["btnText", "btnLink"],
  emits: ["filter", "export-excel"],
  setup(_, { emit }) {
    const searchTerm = ref("");
    const activeFilters = ref<ExpenseFilters>(createEmptyFilters());
    const filters = ref<ExpenseFilters>(createEmptyFilters());
    const warehouses = ref<Warehouse[]>([]);
    const categories = ref<ExpenseCategory[]>([]);

    const fetchFilterData = async () => {
      try {
        const [warehouseData, categoryData] = await Promise.all([
          warehouseService.getWarehouses(),
          expenseService.getExpenseCategories(),
        ]);

        warehouses.value = warehouseData;
        categories.value = categoryData;
      } catch (error) {
        console.error("Error fetching expense filter data:", error);
      }
    };

    const emitFilter = () => {
      emit("filter", {
        ...activeFilters.value,
        q: searchTerm.value,
      });
    };

    const handleSearch = () => {
      emitFilter();
    };

    const applyFilter = () => {
      activeFilters.value = { ...filters.value };
      emitFilter();
    };

    const resetFilter = () => {
      const emptyFilters = createEmptyFilters();
      filters.value = emptyFilters;
      activeFilters.value = emptyFilters;
      emitFilter();
    };

    onMounted(fetchFilterData);

    return {
      searchTerm,
      filters,
      warehouses,
      categories,
      handleSearch,
      applyFilter,
      resetFilter,
    };
  },
});
</script>

<style lang="scss" scoped>
.filter-left {
  button {
    margin-right: 10px;
  }
  .search-area {
    input {
      padding-left: 20px;
      &::placeholder {
        opacity: 0.8;
      }
    }
  }
}
.filter-right {
  a {
    margin-right: 10px;
    &:last-child {
      margin-right: 0;
    }
  }
  div,
  button {
    margin-right: 10px;
  }
}

.form-group select,
.form-group input {
  height: 50px;
}

@media only screen and (max-width: 767px) {
  .filter-left {
    .btn {
      margin-bottom: 15px;
    }
  }
  .filter-right {
    a,
    div,
    button {
      margin-bottom: 10px;
    }
  }
}

@media only screen and (min-width: 768px) and (max-width: 991px) {
  .filter-right {
    width: calc(100% + 20px);
    margin-left: -20px;
  }
}

@media only screen and (max-width: 991px) {
  .filter-left {
    margin-bottom: 20px;
  }
}

@media only screen and (min-width: 992px) {
  .filter-left {
    .search-area {
      width: 320px;
    }
  }
}

@media only screen and (min-width: 1400px) {
  .filter-left {
    button {
      margin-right: 15px;
    }
    .search-area {
      width: 320px;
    }
  }
  .filter-right {
    a,
    div,
    button {
      margin-right: 15px;
    }
  }
}
</style>
